import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  HostListener,
  SimpleChanges,
} from '@angular/core';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import {
  ChildTableColumn,
  ChildTableConfig,
  ChildTableRow,
  CellPosition,
  SortConfig,
  FilterConfig,
  AlertModalConfig,
  normalizeRange,
} from './models/child-table.models';
import { RowActionId } from './models/child-table-row-actions.models';
import { ChildTableStateService } from './services/child-table-state.service';
import { ChildTableA11yService } from './services/child-table-a11y.service';
import { ChildTableImportService, ImportResult, ImportColumnDef } from './services/child-table-import.service';

export type ChildTableCoverState =
  | 'ready'
  | 'empty'
  | 'filteredEmpty'
  | 'loading'
  | 'importing'
  | 'saveBlocked'
  | 'error';

export interface CoverError {
  title: string;
  message: string;
  retryAction?: () => void;
}
import { ImportModalResult } from './components/import-modal/import-modal.component';
import { ChildTablePaginationComponent } from './components/pagination/child-table-pagination.component';
import { ChildTablePositioningService } from './services/child-table-positioning.service';
import { ChildTableDndService } from './services/child-table-dnd.service';
import { ChildTableSelectionService } from './services/child-table-selection.service';
import { ChildTableEditingService, SaveEditResult } from './services/child-table-editing.service';
import { ChildTableRowMenuComponent } from './components/row-menu/child-table-row-menu.component';
import { ChildTableColumnMenuComponent } from './components/column-menu/child-table-column-menu.component';
import { ChildTableMenuService } from './services/child-table-menu.service';
import { ChildTableClipboardService } from './services/child-table-clipboard.service';
import { ChildTableKeyboardService, KeyboardContext } from './services/child-table-keyboard.service';

export { AlertModalConfig } from './models/child-table.models';

/**
 * Native Spreadsheet-like Child Table Component
 * Full Figma parity: drag handle, row numbers, actions column, context menu,
 * custom move modal, cell selection, copy/paste, WCAG AA compliant.
 */
@Component({
  selector: 'app-child-table',
  templateUrl: './child-table.component.html',
  styleUrls: ['./child-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    ChildTableStateService,
    ChildTableA11yService,
    ChildTableImportService,
    ChildTablePositioningService,
    ChildTableDndService,
    ChildTableSelectionService,
    ChildTableMenuService,
    ChildTableEditingService,
    ChildTableClipboardService,
    ChildTableKeyboardService,
  ],
})
export class ChildTableComponent<T = any> implements OnInit, OnChanges, OnDestroy {
  @ViewChild('tableElement') tableElement!: ElementRef<HTMLElement>;
  @ViewChild('alertModalDismissBtn') alertModalDismissBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('paginationRef') paginationRef?: ChildTablePaginationComponent;
  
  // --- Inputs ---
  @Input() config!: ChildTableConfig<T>;
  @Input() set data(value: T[]) {
    if (this.initialized) {
      this.stateService.setRows(value);
      if (this.isSearchingOrLoading) {
        this.isSearchingOrLoading = false;
        this.cdr.markForCheck();
      }
    } else {
      this._initialData = value;
    }
  }
  @Input() title = '';
  @Input() tableTitle = '';
  @Input() tableKey = '';
  @Input() searchPlaceholder = 'Find a record';
  @Input() pageSizeOptions = [5, 10, 25, 50, 100];
  /**
   * Total records across all pages (server-side pagination).
   * If omitted, pagination falls back to `data.length`.
   */
  @Input() totalRecords?: number;
  @Input() showAutoSaveStatus = true;
  @Input() reportUrl = '';
  
  // --- Outputs ---
  @Output() cellChange = new EventEmitter<{ rowId: string; columnKey: string; value: any; oldValue: any }>();
  @Output() rowAdd = new EventEmitter<ChildTableRow<T>>();
  @Output() rowDelete = new EventEmitter<string[]>();
  @Output() selectionChange = new EventEmitter<string[]>();
  @Output() sortChange = new EventEmitter<SortConfig | null>();
  @Output() filterChange = new EventEmitter<FilterConfig[]>();
  @Output() rowReorder = new EventEmitter<{ fromIndex: number; toIndex: number }>();
  @Output() save = new EventEmitter<ChildTableRow<T>[]>();
  @Output() validationError = new EventEmitter<{ rowId: string; columnKey: string; error: string }>();
  @Output() importComplete = new EventEmitter<ImportResult<T>>();
  /** Emitted when the user navigates to a different page (0-based). */
  @Output() pageChange = new EventEmitter<number>();
  /** Emitted when the user changes page size. */
  @Output() pageSizeChange = new EventEmitter<number>();
  /** Emitted when the search term changes (for server-side search). */
  @Output() searchChange = new EventEmitter<string>();

  // --- Core state ---
  displayRows: ChildTableRow<T>[] = [];
  columns: ChildTableColumn<T>[] = [];
  selectedIds: string[] = [];
  searchTerm = '';
  pageSize = 10;
  currentPage = 0;
  get focusedCell(): CellPosition | null { return this.keyboardService.focusedCell; }
  set focusedCell(v: CellPosition | null) { this.keyboardService.focusedCell = v; }
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;
  selectAll = false;
  lastUpdated: Date | null = null;
  isSaving = false;

  // --- Cover state tracking ---
  isSearchingOrLoading = false;
  isImportInProgress = false;
  recoverableError: CoverError | null = null;
  hasUserInteracted = false;

  // --- Import progress tracking ---
  importProgress: {
    phase: 'adding' | 'saving';
    current: number;
    total: number;
    cancelled: boolean;
  } | null = null;

  get hasUnsavedChanges(): boolean {
    return this.stateService.getDirtyRows().length > 0;
  }

  /** Convenience alias — template reads editingService.editingCell directly,
   *  but component code can use this shorter form. */
  get editingCell() { return this.editingService.editingCell; }
  get editingValue() { return this.editingService.editingValue; }
  set editingValue(v: any) { this.editingService.editingValue = v; }

  // --- Column menu ---
  @ViewChild('columnMenuDiv') columnMenuComp!: ChildTableColumnMenuComponent;
  hiddenColumns: Set<string> = new Set();
  
  // --- Row context menu (Actions column) ---
  @ViewChild('rowMenuComp') rowMenuComp!: ChildTableRowMenuComponent;

  // --- Custom Move modal ---
  customMoveOpen = false;
  customMoveRowId: string | null = null;
  
  // --- Import modal ---
  openImportModal = false;

  get importModalColumns(): Array<ImportColumnDef & { fieldName: string }> {
    return this.columns
      .filter(c => c.editable !== false && c.type !== 'attachment')
      .map(c => ({
        key: c.key,
        label: c.label,
        type: c.type as string,
        selectOptions: c.selectOptions?.map(o => ({
          value: String(o.value),
          label: o.label,
        })),
        required: !!(c.validation?.some(v => v.type === 'required')),
        isUnique: !!c.isUnique,
        fieldName: c.fieldName ?? c.key,
        min: c.validation?.find(v => v.type === 'min')?.value,
        max: c.validation?.find(v => v.type === 'max')?.value,
        pattern: c.validation?.find(v => v.type === 'pattern')?.value,
      }));
  }

  /** Convenience alias — template and internal code read clipboardService.pasteErrorLocked. */
  get pasteErrorLocked(): boolean { return this.clipboardService.pasteErrorLocked; }

  get isEditable(): boolean {
    return this.config?.editable !== false && !this.config?.readonly;
  }

  get isTrulyEmpty(): boolean {
    const localRows = this.stateService.getState().rows.length;
    if (localRows > 0) return false;
    if (this.config?.serverSidePagination) {
      const total = Number(this.totalRecords);
      if (Number.isFinite(total)) return total === 0;
    }
    return true;
  }

  get isFilteredEmpty(): boolean {
    if (this.displayRows.length > 0) return false;
    // Any active filter / search means the empty grid is a filter result, not "truly empty"
    return this.hasActiveFilters || !this.isTrulyEmpty;
  }

  /**
   * Single source of truth for what the child table should display.
   * Precedence: error > importing > loading > saveBlocked > filteredEmpty > empty > ready
   *
   * Key invariant: if any filter or search is active and rows are 0,
   * the state is ALWAYS 'filteredEmpty' — never 'empty'.
   * This prevents the "No records yet" CTA from appearing when the
   * user simply needs to clear their filters.
   */
  get coverState(): ChildTableCoverState {
    if (this.recoverableError) return 'error';
    if (this.isImportInProgress) return 'importing';
    if (this.isSearchingOrLoading) return 'loading';
    if (this.pasteErrorLocked) return 'saveBlocked';
    if (this.isFilteredEmpty) return 'filteredEmpty';
    if (this.isTrulyEmpty && this.isEditable && !this.hasUserInteracted) return 'empty';
    return 'ready';
  }

  get importProgressPercent(): number {
    if (!this.importProgress || this.importProgress.total === 0) return 0;
    return Math.round((this.importProgress.current / this.importProgress.total) * 100);
  }

  get importProgressLabel(): string {
    if (!this.importProgress) return '';
    if (this.importProgress.phase === 'adding') {
      return `Adding row ${this.importProgress.current} of ${this.importProgress.total}`;
    }
    return `Saving batch ${this.importProgress.current} of ${this.importProgress.total}`;
  }

  // --- Generic alert modal (WCAG AA alertdialog) ---
  alertModal: AlertModalConfig | null = null;
  private alertReturnFocus: CellPosition | null = null;

  get headerInteractionMode(): boolean { return this.keyboardService.headerInteractionMode; }
  get headerInteractionColIndex(): number | null { return this.keyboardService.headerInteractionColIndex; }

  /** Unique instance ID for ARIA id references */
  readonly tableInstanceId = 'ct-' + Math.random().toString(36).slice(2, 8);

  // --- Internal ---
  private readonly destroy$ = new Subject<void>();
  private readonly columnWidthOverrides = new Map<string, number>();
  private resizeSub: Subscription | null = null;
  private resizing: { columnKey: string; startX: number; startWidth: number } | null = null;
  private readonly searchSubject$ = new Subject<string>();
  private initialized = false;
  private _initialData: T[] = [];
  isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.platform || navigator.userAgent);

  get modKey(): string {
    return this.isMac ? '⌘' : 'Ctrl';
  }

  constructor(
    public stateService: ChildTableStateService<T>,
    public a11yService: ChildTableA11yService,
    public importService: ChildTableImportService,
    public dndService: ChildTableDndService<T>,
    public selectionService: ChildTableSelectionService,
    public menuService: ChildTableMenuService<T>,
    public editingService: ChildTableEditingService<T>,
    public clipboardService: ChildTableClipboardService<T>,
    public keyboardService: ChildTableKeyboardService<T>,
    private readonly cdr: ChangeDetectorRef,
    private readonly positioningService: ChildTablePositioningService,
    private readonly elementRef: ElementRef<HTMLElement>
  ) {}

  // ============================================================
  // Lifecycle
  // ============================================================

  ngOnInit(): void {
    this.initializeTable();
    this.syncDndConfig();
    this.subscribeToStateChanges();
    this.setupSearch();
    this.setupAutoSave();
    this.initialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Allow parent containers (like demos / runtime integration) to load metadata asynchronously
    // and then update the table config (e.g., columns) without requiring a full route reload.
    if (changes['config'] && this.initialized && this.config) {
      // Close any floating UI and cancel editing to avoid stale references.
      this.cleanupDropdownState();
      this.menuService.closeRowMenu();
      if (this.editingCell) {
        this.cancelEdit();
      }

      // Preserve pagination across config refreshes (e.g. when columns are rebuilt
      // after discovery in server-side pagination mode).
      const prevPageSize = this.pageSize;
      const prevCurrentPage = this.currentPage;

      const currentData = this.displayRows.map((r) => r.data);
      this.stateService.initialize(this.config, currentData, prevPageSize);

      // Restore page — clamped to valid range for the (possibly new) dataset.
      const maxPage = Math.max(0, this.totalPages - 1);
      const restoredPage = Math.min(prevCurrentPage, maxPage);
      this.stateService.setCurrentPage(restoredPage);
      this.currentPage = restoredPage;
      this.pageSize = prevPageSize;

      this.syncDndConfig();
      this.cdr.markForCheck();
      setTimeout(() => this.keyboardService.resetRovingTabindex(this.tableElement), 0);
    }
  }

  ngOnDestroy(): void {
    this.positioningService.destroy();
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeSub?.unsubscribe();
    if (this.resizing) {
      globalThis.document.body.style.cursor = '';
      globalThis.document.body.style.userSelect = '';
    }
    this.clipboardService.destroy();
  }

  // ============================================================
  // Initialization
  // ============================================================

  private initializeTable(): void {
    if (!this.config) {
      console.warn('ChildTableComponent: config is required');
      return;
    }
    // Default page size:
    // - server-side pagination: backend default is typically 50
    // - client-side: start smaller (10) unless virtualize is enabled
    this.pageSize = this.config.serverSidePagination ? 50 : (this.config.virtualize ? 50 : 10);
    this.currentPage = 0;
    this.stateService.initialize(this.config, this._initialData, this.pageSize);
    this.stateService.setCurrentPage(0);
  }

  private syncDndConfig(): void {
    this.dndService.rowReorderable = !!this.config?.rowReorderable;
    this.dndService.readonly = !!this.config?.readonly;
    this.dndService.pasteErrorLocked = this.pasteErrorLocked;
  }

  private previousCoverState: ChildTableCoverState = 'ready';

  private subscribeToStateChanges(): void {
    this.stateService.displayRows$.pipe(takeUntil(this.destroy$)).subscribe((rows) => {
      this.displayRows = rows;
      this.announceCoverStateTransition();
      this.cdr.markForCheck();
    });

    this.stateService.selectedIds$.pipe(takeUntil(this.destroy$)).subscribe((ids) => {
      this.selectedIds = ids;
      this.selectAll = ids.length > 0 && ids.length === this.displayRows.length;
      this.selectionChange.emit(ids);
      this.cdr.markForCheck();
    });

    this.stateService.state.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.columns = state.columns;
      this.sortColumn = state.sort?.column || null;
      this.sortDirection = state.sort?.direction || null;
      this.pageSize = state.pageSize;
      this.currentPage = state.currentPage;
      this.cdr.markForCheck();
    });

    this.dndService.reorder$.pipe(takeUntil(this.destroy$)).subscribe((reorder) => {
      this.rowReorder.emit(reorder);
      this.cdr.markForCheck();
    });
  }

  private setupSearch(): void {
    this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe((term) => {
      this.stateService.setSearchTerm(term);
      this.a11yService.announceFilterResults(this.displayRows.length);
    });
  }

  private setupAutoSave(): void {
    this.stateService.saveChanges$.pipe(takeUntil(this.destroy$)).subscribe((dirtyRows) => {
      if (this.pasteErrorLocked) return;
      if (dirtyRows.length > 0) {
        this.save.emit(dirtyRows);
        this.stateService.markAllClean();
        this.lastUpdated = new Date();
        this.a11yService.announceEditSaved();
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Public API: replace temp IDs with real database IDs after sync.
   * Call this from the parent after a successful syncdata response.
   * Example: childTable.applySyncResult({ 'row_temp_123': '50' });
   */
  applySyncResult(addedRecs: Record<string, string>): void {
    if (addedRecs && Object.keys(addedRecs).length > 0) {
      this.stateService.replaceRowIds(addedRecs);
      this.cdr.markForCheck();
    }
  }

  /**
   * Public API: revert a row to its last known good state (originalData).
   * Call from the parent when a save fails to undo the bad edit.
   */
  revertRow(rowId: string): void {
    this.stateService.revertRow(rowId);
    this.cdr.markForCheck();
  }

  /**
   * Public API: get the current data for a row by ID.
   * Returns the live row data object or undefined if not found.
   */
  getRowData(rowId: string): any | undefined {
    const row = this.stateService.getRow(rowId);
    return row?.data;
  }

  /**
   * Public API: programmatically set a cell value (e.g. from a form-rule "change" action).
   * Marks the row as dirty and emits cellChange.
   */
  setCellValue(rowId: string, columnKey: string, value: any): void {
    const row = this.stateService.getRow(rowId);
    if (!row) return;
    const oldValue = (row.data as any)[columnKey];
    if (oldValue === value) return;
    this.stateService.updateCell(rowId, columnKey, value);
    this.cellChange.emit({ rowId, columnKey, value, oldValue });
    this.cdr.markForCheck();
  }

  /** Manual save button -- immediately flushes all dirty rows. */
  onManualSave(): void {
    const dirtyRows = this.stateService.getDirtyRows();
    if (dirtyRows.length === 0) return;
    this.isSaving = true;
    this.cdr.markForCheck();
    this.save.emit(dirtyRows);
    this.stateService.markAllClean();
    this.lastUpdated = new Date();
    this.a11yService.announce('Saving changes...');
    setTimeout(() => {
      this.isSaving = false;
      this.cdr.markForCheck();
    }, 1000);
  }

  // ============================================================
  // Column widths (CSS Grid column template)
  // ============================================================

  /** CSS grid-template-columns value for every .grid-row. */
  get gridTemplateColumns(): string {
    const parts: string[] = [];
    // Row-select column (fixed 56px)
    parts.push('56px');
    // Actions column (fixed 80px) if enabled
    if (this.config.showRowActions !== false) {
      parts.push('80px');
    }
    // Data columns: configured width + header button widths.
    // The sort button (28px) and menu button (32px) sit beside the label
    // in the header row, so the column must be wide enough for both.
    for (const col of this.visibleColumns) {
      const dataW = this.getColumnWidthPx(col);
      const sortW = col.sortable ? 36 : 0;
      const menuW = 38;
      const colW = dataW + sortW + menuW;
      parts.push(`minmax(${colW}px, 1fr)`);
    }
    return parts.join(' ');
  }

  getColumnMinWidthPx(column: ChildTableColumn<T>): number {
    return typeof column.minWidth === 'number' ? column.minWidth : 80;
  }

  getColumnMaxWidthPx(_column: ChildTableColumn<T>): number {
    return 2000;
  }

  getColumnWidthPx(column: ChildTableColumn<T>): number {
    const override = this.columnWidthOverrides.get(column.key);
    if (typeof override === 'number') return override;
    if (typeof column.width === 'number') return column.width;
    if (typeof column.minWidth === 'number') return column.minWidth;
    return 100;
  }

  private setColumnWidthPx(columnKey: string, widthPx: number): void {
    const col = this.visibleColumns.find((c) => c.key === columnKey);
    const minW = col ? this.getColumnMinWidthPx(col) : 80;
    const maxW = col ? this.getColumnMaxWidthPx(col) : 800;
    this.columnWidthOverrides.set(columnKey, Math.max(minW, Math.min(maxW, Math.round(widthPx))));
    this.cdr.markForCheck();
  }

  getColumnCount(): number {
    let count = this.visibleColumns.length;
    if (this.config.selectable || this.config.rowReorderable) count++;
    if (this.config.showRowActions !== false) count++;
    return count;
  }

  /** Number of utility columns before data columns (for aria-colindex offset). */
  get utilityColumnCount(): number {
    let n = 0;
    if (this.config.selectable || this.config.rowReorderable) n++;
    if (this.config.showRowActions !== false) n++;
    return n;
  }

  /** 1-based aria-colindex for data columns, offset by utility columns. */
  getAriaColIndex(dataColIndex: number): number {
    return dataColIndex + this.utilityColumnCount + 1;
  }

  /** Build the aria-describedby ID list for the grid element. */
  getGridDescribedBy(): string {
    const ids: string[] = ['grid-instructions-' + this.tableInstanceId];
    if (this.config.description) {
      ids.unshift('table-desc-' + this.tableInstanceId);
    }
    return ids.join(' ');
  }

  /** Skip table: programmatically find and focus the next meaningful content outside the component. */
  onSkipTable(event: Event): void {
    event.preventDefault();
    const host = this.elementRef.nativeElement;
    const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const headings = 'h1, h2, h3, h4, h5, h6';

    const tryFocus = (el: Element): boolean => {
      if (el.matches(focusable)) {
        (el as HTMLElement).focus();
        return true;
      }
      if (el.matches(headings)) {
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        (el as HTMLElement).focus();
        return true;
      }
      const target = el.querySelector(`${focusable}, ${headings}`);
      if (target) {
        if (target.matches(headings) && !target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        (target as HTMLElement).focus();
        return true;
      }
      return false;
    };

    let sibling = host.nextElementSibling;
    while (sibling) {
      if (tryFocus(sibling)) {
        this.a11yService.announce('Skipped table');
        return;
      }
      sibling = sibling.nextElementSibling;
    }

    let parent = host.parentElement;
    while (parent && parent !== document.body) {
      let next = parent.nextElementSibling;
      while (next) {
        if (tryFocus(next)) {
          this.a11yService.announce('Skipped table');
          return;
        }
        next = next.nextElementSibling;
      }
      parent = parent.parentElement;
    }

    const fallback = document.getElementById('after-table-' + this.tableInstanceId);
    if (fallback) fallback.focus();
    this.a11yService.announce('Skipped table');
  }

  // ============================================================
  // Column resizing (mouse + keyboard)
  // ============================================================

  onResizeMouseDown(event: MouseEvent, columnKey: string): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const col = this.visibleColumns.find((c) => c.key === columnKey);
    const startWidth = col ? this.getColumnWidthPx(col) : 100;
    this.resizing = { columnKey, startX: event.clientX, startWidth };
    this.resizeSub?.unsubscribe();

    const move$ = fromEvent<MouseEvent>(globalThis.document, 'mousemove');
    const up$ = fromEvent<MouseEvent>(globalThis.document, 'mouseup');

    globalThis.document.body.style.cursor = 'col-resize';
    globalThis.document.body.style.userSelect = 'none';

    this.resizeSub = move$.pipe(takeUntil(up$), takeUntil(this.destroy$)).subscribe((moveEvt) => {
      if (!this.resizing) return;
      this.setColumnWidthPx(this.resizing.columnKey, this.resizing.startWidth + moveEvt.clientX - this.resizing.startX);
    });

    up$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.resizing = null;
      this.resizeSub?.unsubscribe();
      this.resizeSub = null;
      globalThis.document.body.style.cursor = '';
      globalThis.document.body.style.userSelect = '';
    });
  }

  onResizeKeydown(event: KeyboardEvent, columnKey: string): void {
    const col = this.visibleColumns.find((c) => c.key === columnKey);
    if (!col) return;

    // Escape and Enter/Space are handled by onHeaderWidgetKeydown when
    // in interaction mode; only handle arrow/Home/End resize keys here.
    if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ' || event.key === 'Tab') return;

    const step = event.shiftKey ? 50 : 10;
    const current = this.getColumnWidthPx(col);

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.setColumnWidthPx(columnKey, current - step);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.setColumnWidthPx(columnKey, current + step);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.setColumnWidthPx(columnKey, this.getColumnMinWidthPx(col));
    } else if (event.key === 'End') {
      event.preventDefault();
      this.setColumnWidthPx(columnKey, this.getColumnMaxWidthPx(col));
    }
  }

  // ============================================================
  // Global keyboard handler — delegates to ChildTableKeyboardService
  // ============================================================

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    this.keyboardService.onKeydown(event, this.buildKeyboardContext());
  }

  @HostListener('paste', ['$event'])
  onPasteEvent(event: ClipboardEvent): void {
    if (this.pasteErrorLocked) { event.preventDefault(); return; }
    this.onPasteAction(event);
    this.cdr.markForCheck();
  }

  // ============================================================
  // Search & pagination
  // ============================================================

  onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchSubject$.next(value);
    if (this.config?.serverSidePagination) {
      this.searchChange.emit(value);
    } else {
      this.goToPage(0);
    }
  }

  onPageSizeChange(size: number | string): void {
    // Defensive coercion: Angular <select> may emit a string even with [ngValue].
    const next = Number(size) || this.pageSize || 10;
    // Let the state subscription be the single source of truth for this.pageSize
    // to avoid race conditions between the direct assignment and the async state update.
    this.stateService.setPageSize(next);
    this.stateService.setCurrentPage(0);
    // Clear selection on page change -- stale ranges from old page are invalid
    this.selectionService.cellRange = null;
    this.focusedCell = null;
    this.a11yService.announce(`Showing ${next} rows`);
    this.pageSizeChange.emit(next);
  }

  private resetPaginationAfterImport(): void {
    const clientDefault = this.config?.virtualize ? 50 : 10;
    this.pageSize = clientDefault;
    this.currentPage = 0;
    this.stateService.setPageSize(clientDefault);
    this.stateService.setCurrentPage(0);
    this.cdr.markForCheck();
  }

  goToPage(page: number): void {
    const clamped = Math.max(0, Math.min(this.totalPages - 1, Math.floor(page)));
    if (clamped === this.currentPage) return;
    this.currentPage = clamped;
    this.stateService.setCurrentPage(clamped);

    this.stateService.resetSelectionAnchor();
    this.selectionService.cellRange = null;
    this.focusedCell = null;
    this.keyboardService.resetLastFocusedRowIndex();
    this.a11yService.announce(`Page ${clamped + 1} of ${this.totalPages}`);
    this.cdr.markForCheck();

    if (this.config?.serverSidePagination) {
      this.pageChange.emit(clamped);
    }
    setTimeout(() => {
      this.keyboardService.resetRovingTabindex(this.tableElement);
      if (this.visibleRows.length > 0) {
        this.keyboardService.focusCellByPosition({ rowIndex: 0, columnIndex: 0 }, this.tableElement);
      }
    }, 0);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  // ============================================================
  // Selection
  // ============================================================

  onSelectAll(): void {
    if (this.selectAll) {
      this.stateService.clearSelection();
    } else {
      this.stateService.selectAll();
    }
    this.a11yService.announceSelection(this.selectAll ? 0 : this.displayRows.length);
  }

  onRowSelect(rowId: string): void {
    this.stateService.toggleRowSelection(rowId);
  }

  onRowSelectClick(event: MouseEvent, rowId: string, rowIndex: number): void {
    this.stateService.toggleRowSelectionWithShift(rowId, rowIndex, event.shiftKey, this.visibleRows);
  }


  // ============================================================
  // Cell range selection — delegates to ChildTableSelectionService
  // ============================================================

  /**
   * Handle cell click for range selection.
   * - Click = select single cell (anchor = focus = clicked cell)
   * - Shift+Click = extend range (keep anchor, move focus)
   * Also sets focusedCell so paste always has a target.
   */
  onCellClick(event: MouseEvent, _rowId: string, _colKey: string, rowIndex: number, colIndex: number): void {
    if (this.pasteErrorLocked) return;
    if (this.editingCell) {
      if (!this.saveEditQuietly()) return;
    }

    this.focusedCell = { rowIndex, columnIndex: colIndex };

    if (event.shiftKey && this.selectionService.cellRange) {
      this.selectionService.cellRange = { ...this.selectionService.cellRange, focusRow: rowIndex, focusCol: colIndex };
      this.selectionService.announceRangeIfMultiCell();
    } else {
      this.selectionService.cellRange = { anchorRow: rowIndex, anchorCol: colIndex, focusRow: rowIndex, focusCol: colIndex };
    }
    this.cdr.markForCheck();
  }

  clearCellRange(): void {
    this.selectionService.clearCellRange();
    this.cdr.markForCheck();
  }

  // ============================================================
  // Mouse drag-to-select — thin wrappers delegating to service
  // ============================================================

  onCellMouseDown(event: MouseEvent, rowIndex: number, colIndex: number): void {
    if (event.button !== 0) return;
    if (this.pasteErrorLocked) return;

    const target = event.target as HTMLElement;
    if (target.closest('.cell-listbox') || target.closest('.cell-listbox-wrapper')) return;

    if (this.editingCell) {
      if (!this.saveEditQuietly()) return;
    }

    this.selectionService.onCellMouseDown(event.shiftKey, rowIndex, colIndex);
    this.focusedCell = { rowIndex, columnIndex: colIndex };
    this.cdr.markForCheck();
  }

  onCellMouseEnter(rowIndex: number, colIndex: number): void {
    if (this.selectionService.onCellMouseEnter(rowIndex, colIndex)) {
      this.focusedCell = { rowIndex, columnIndex: colIndex };
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this.selectionService.onDocumentMouseUp()) {
      if (this.focusedCell) {
        this.keyboardService.focusCellByPosition(this.focusedCell, this.tableElement);
      }
    }
  }

  // ============================================================
  // Copy / Cut / Paste — delegates to ChildTableClipboardService
  // ============================================================

  onCopyAction(): void {
    this.clipboardService.onCopyAction(
      this.visibleRows, this.visibleColumns, this.displayRows, this.selectedIds,
    );
    this.paginationRef?.showNotification({ text: 'Copied to clipboard', type: 'success' });
  }

  onCutAction(): void {
    this.clipboardService.onCutAction(
      this.visibleRows, this.visibleColumns, this.displayRows, this.selectedIds, this.config,
    );
    this.cdr.markForCheck();
  }

  /**
   * Clear editable cell values in the current selection without copying to clipboard.
   * If a cell range is active, clears cells within the range.
   * If rows are checkbox-selected, clears all editable cells in those rows.
   */
  onClearCells(): void {
    if (this.config.readonly) return;

    const cellRange = this.selectionService.cellRange;
    if (cellRange) {
      const { minRow, maxRow, minCol, maxCol } = normalizeRange(cellRange);
      for (let r = minRow; r <= maxRow; r++) {
        const row = this.visibleRows[r];
        if (!row) continue;
        for (let c = minCol; c <= maxCol; c++) {
          const col = this.visibleColumns[c];
          if (!col?.editable || col.type === 'file') continue;
          const clearVal = col.type === 'number' || col.type === 'currency' ? null : '';
          this.stateService.updateCell(row.id, col.key, clearVal);
        }
      }
      this.a11yService.announce('Cleared');
      this.cdr.markForCheck();
      return;
    }

    const selectedIds = new Set(this.selectedIds);
    if (selectedIds.size > 0) {
      for (const row of this.visibleRows) {
        if (!selectedIds.has(row.id)) continue;
        for (const col of this.visibleColumns) {
          if (!col.editable || col.type === 'file') continue;
          const clearVal = col.type === 'number' || col.type === 'currency' ? null : '';
          this.stateService.updateCell(row.id, col.key, clearVal);
        }
      }
      this.a11yService.announce(`Cleared ${selectedIds.size} row${selectedIds.size === 1 ? '' : 's'}`);
      this.cdr.markForCheck();
    }
  }

  onPasteAction(event: ClipboardEvent): void {
    if (!this.focusedCell) return;
    this.clipboardService.onPasteAction(
      event,
      this.focusedCell.rowIndex,
      this.focusedCell.columnIndex,
      this.visibleRows,
      this.visibleColumns,
      this.config,
      this.isMac,
      this.modKey,
      (cfg) => this.showAlertModal(cfg),
      () => this.dismissAlertModal(),
    );
    this.cdr.markForCheck();
  }

  undoBadPaste(): void {
    this.clipboardService.undoBadPaste(() => this.dismissAlertModal());
    this.cdr.markForCheck();
  }

  // ============================================================
  // Generic Alert Modal (WCAG AA alertdialog)
  // ============================================================

  showAlertModal(config: AlertModalConfig): void {
    this.alertReturnFocus = this.focusedCell ? { ...this.focusedCell } : null;
    this.alertModal = config;
    this.cdr.markForCheck();
    setTimeout(() => this.alertModalDismissBtn?.nativeElement?.focus(), 0);
  }

  /**
   * Public API: Show a save error notification in the HUD.
   * Call from the parent component when syncdata returns errors.
   */
  showSaveError(errors: string[]): void {
    const message = errors.length === 1
      ? errors[0]
      : errors.map((e, i) => `${i + 1}. ${e}`).join('; ');
    if (this.paginationRef) {
      this.paginationRef.showNotification(
        { text: message || 'Unable to save. Please try again.', type: 'error' },
      );
    } else {
      // Fallback to modal if pagination isn't mounted (e.g. loading state)
      this.showAlertModal({
        title: 'Unable to save',
        message: message || 'An error occurred while saving. Please try again.',
        buttonLabel: 'OK',
        icon: 'warning',
        onDismiss: () => this.dismissAlertModal(),
      });
    }
  }

  /**
   * Public API: Clear the save error notification from the HUD.
   * Call from the parent component after a successful save.
   */
  clearSaveError(): void {
    this.paginationRef?.dismissNotification();
  }

  dismissAlertModal(): void {
    if (!this.alertModal) return;
    const returnPos = this.alertReturnFocus;
    this.alertModal = null;
    this.alertReturnFocus = null;
    this.cdr.markForCheck();
    if (returnPos) {
      setTimeout(() => this.keyboardService.focusCellByPosition(returnPos, this.tableElement), 0);
    }
  }

  /** Keyboard handler for the alert modal (focus trap with proper cycling). */
  onAlertModalKeydown(event: KeyboardEvent): void {
    if (this.pasteErrorLocked && this.a11yService.isUndoShortcut(event)) {
      event.preventDefault();
      this.undoBadPaste();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!this.pasteErrorLocked) {
        this.alertModal?.onDismiss();
      }
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const dialog = (event.target as HTMLElement).closest('.ct-alert-modal');
      if (!dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusable.length === 0) return;
      const currentIdx = focusable.indexOf(event.target as HTMLElement);
      const nextIdx = event.shiftKey
        ? (currentIdx <= 0 ? focusable.length - 1 : currentIdx - 1)
        : (currentIdx >= focusable.length - 1 ? 0 : currentIdx + 1);
      focusable[nextIdx].focus();
    }
  }

  private showReadonlyFieldAlert(columnLabel: string): void {
    this.showAlertModal({
      title: 'This field cannot be edited',
      message: `The "${columnLabel}" column is read-only. This field is managed by the system and cannot be changed manually.`,
      buttonLabel: 'OK',
      icon: 'info',
      onDismiss: () => this.dismissAlertModal(),
    });
    this.a11yService.announce(
      `${columnLabel} is a read-only field and cannot be edited.`,
      'assertive',
    );
  }

  onFileRemove(rowId: string, columnKey: string, event: Event): void {
    event.stopPropagation();
    const row = this.displayRows.find((r) => r.id === rowId);
    if (!row) return;
    const oldValue = (row.data as any)[columnKey];
    this.stateService.updateCell(rowId, columnKey, '');
    this.cellChange.emit({ rowId, columnKey, value: '', oldValue });
    this.cdr.markForCheck();
  }

  // ============================================================
  // Row context menu (Actions column) — delegates to ChildTableMenuService
  // ============================================================

  toggleRowMenu(rowId: string, rowIndex: number, event: MouseEvent): void {
    const opened = this.menuService.toggleRowMenu(
      rowId, rowIndex, event, this.pasteErrorLocked, this.displayRows.length,
    );
    if (opened) this.positionRowMenuAfterDetect();
  }

  private openRowMenuWithTrigger(rowId: string, rowIndex: number, trigger: HTMLElement): void {
    this.cleanupDropdownState();
    const opened = this.menuService.openRowMenuWithTrigger(
      rowId, rowIndex, trigger, this.displayRows.length,
    );
    if (opened) this.positionRowMenuAfterDetect();
  }

  private positionRowMenuAfterDetect(): void {
    this.cdr.detectChanges();
    const menuEl = this.rowMenuComp?.nativeElement;
    if (menuEl) {
      this.menuService.positionRowMenu(menuEl);
    }
  }

  closeRowMenu(): void {
    this.menuService.closeRowMenu();
    this.cdr.markForCheck();
  }

  onRowAction(actionId: RowActionId): void {
    const result = this.menuService.onRowAction(actionId, this.displayRows, this.visibleColumns);
    this.cdr.markForCheck();
    if (!result) return;

    switch (result.actionId) {
      case 'moveUp':
        this.contextMoveUp();
        return;
      case 'moveDown':
        this.contextMoveDown();
        return;
      case 'customMove':
        this.openCustomMove(result.rowId);
        this.menuService.closeRowMenu();
        this.cdr.markForCheck();
        return;
      case 'insertAbove':
        this.insertRowAbove();
        return;
      case 'insertBelow':
        this.insertRowBelow();
        return;
      case 'delete':
        this.deleteRowAction();
        return;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.editingCell && !target.closest('ct-editor-host') && !target.closest('[aria-modal="true"]') && !target.closest('.cell-listbox-wrapper') && !target.closest('.cell-listbox') && !target.closest('.cell-input') && !target.closest('child-table-cell-editor')) {
      this.saveEditQuietly();
    }
    this.cdr.markForCheck();
  }

  // --- Context menu actions (orchestration stays in component) ---

  contextMoveUp(): void {
    if (!this.menuService.openRowMenuId || this.menuService.openRowMenuIndex <= 0) return;
    this.onMoveRowUp(this.menuService.openRowMenuId);
    this.menuService.openRowMenuIndex--;
    this.menuService.closeRowMenu();
    this.cdr.markForCheck();
  }

  contextMoveDown(): void {
    if (!this.menuService.openRowMenuId || this.menuService.openRowMenuIndex >= this.displayRows.length - 1) return;
    this.onMoveRowDown(this.menuService.openRowMenuId);
    this.menuService.openRowMenuIndex++;
    this.menuService.closeRowMenu();
    this.cdr.markForCheck();
  }

  insertRowAbove(): void {
    if (this.menuService.openRowMenuIndex < 0) return;
    const insertIdx = this.menuService.openRowMenuIndex;
    const newRow = this.stateService.addRow(undefined, insertIdx);
    this.a11yService.announceRowAdded(insertIdx + 1);
    this.rowAdd.emit(newRow);
    this.menuService.closeRowMenu();
    this.cdr.markForCheck();
    this.navigateToRowPage(newRow.id);
  }

  insertRowBelow(): void {
    if (this.menuService.openRowMenuIndex < 0) return;
    const insertIdx = this.menuService.openRowMenuIndex + 1;
    const newRow = this.stateService.addRow(undefined, insertIdx);
    this.a11yService.announceRowAdded(insertIdx + 1);
    this.rowAdd.emit(newRow);
    this.menuService.closeRowMenu();
    this.cdr.markForCheck();
    this.navigateToRowPage(newRow.id);
  }

  /** Navigate to the page containing a row and focus its first cell. */
  private navigateToRowPage(rowId: string): void {
    const globalIdx = this.displayRows.findIndex(r => r.id === rowId);
    if (globalIdx < 0) return;
    const targetPage = Math.floor(globalIdx / this.pageSize);
    if (this.currentPage !== targetPage) {
      this.goToPage(targetPage);
    }
    setTimeout(() => {
      const idx = this.visibleRows.findIndex(r => r.id === rowId);
      if (idx >= 0) this.keyboardService.focusCellByPosition({ rowIndex: idx, columnIndex: 0 }, this.tableElement);
    }, 50);
  }

  deleteRowAction(): void {
    if (!this.menuService.openRowMenuId) return;
    this.stateService.deleteRows([this.menuService.openRowMenuId]);
    this.a11yService.announceRowsDeleted(1);
    this.rowDelete.emit([this.menuService.openRowMenuId]);
    this.menuService.closeRowMenu();
    this.cdr.markForCheck();
  }

  // ============================================================
  // Custom Move modal
  // ============================================================

  openCustomMove(rowId: string): void {
    this.customMoveOpen = true;
    this.customMoveRowId = rowId;
    this.cdr.markForCheck();
  }

  closeCustomMove(): void {
    this.customMoveOpen = false;
    this.customMoveRowId = null;
    this.cdr.markForCheck();
  }

  onCustomMoveSubmit(targetRow: number): void {
    if (!this.customMoveRowId) return;
    const fromIndex = this.displayRows.findIndex((r) => r.id === this.customMoveRowId);
    const toIndex = targetRow - 1;
    if (fromIndex >= 0 && toIndex >= 0 && toIndex < this.displayRows.length) {
      this.stateService.moveRow(fromIndex, toIndex);
      this.a11yService.announceRowMoved(fromIndex + 1, toIndex + 1);
      this.rowReorder.emit({ fromIndex, toIndex });
    }
    this.closeCustomMove();
  }

  // ============================================================
  // Toolbar move arrows + move-to-position
  // ============================================================

  canMoveSelectedUp(): boolean {
    if (this.selectedIds.length !== 1) return false;
    const idx = this.displayRows.findIndex((r) => r.id === this.selectedIds[0]);
    return idx > 0;
  }

  canMoveSelectedDown(): boolean {
    if (this.selectedIds.length !== 1) return false;
    const idx = this.displayRows.findIndex((r) => r.id === this.selectedIds[0]);
    return idx >= 0 && idx < this.displayRows.length - 1;
  }

  moveSelectedRowUp(): void {
    if (this.selectedIds.length !== 1) return;
    this.onMoveRowUp(this.selectedIds[0]);
  }

  moveSelectedRowDown(): void {
    if (this.selectedIds.length !== 1) return;
    this.onMoveRowDown(this.selectedIds[0]);
  }

  onToolbarMoveToPosition(target: number): void {
    if (this.selectedIds.length !== 1) return;
    const fromIndex = this.displayRows.findIndex((r) => r.id === this.selectedIds[0]);
    const toIndex = target - 1;
    if (fromIndex >= 0 && toIndex >= 0 && toIndex < this.displayRows.length) {
      this.stateService.moveRow(fromIndex, toIndex);
      this.a11yService.announceRowMoved(fromIndex + 1, toIndex + 1);
      this.rowReorder.emit({ fromIndex, toIndex });
    }
  }

  // ============================================================
  // Sort
  // ============================================================

  onSort(columnKey: string): void {
    const column = this.columns.find((c) => c.key === columnKey);
    if (!column?.sortable) return;
    this.stateService.toggleSort(columnKey);
    const state = this.stateService.getState();
    this.sortChange.emit(state.sort);
    this.a11yService.announceSort(column.label, state.sort?.direction || null);
  }

  getSortAriaSort(columnKey: string): 'ascending' | 'descending' | 'none' {
    if (this.sortColumn !== columnKey) return 'none';
    return this.sortDirection === 'asc' ? 'ascending' : 'descending';
  }

  // ============================================================
  // Row operations
  // ============================================================

  onAddRow(): void {
    if (this.pasteErrorLocked) return;
    this.hasUserInteracted = true;
    const newRow = this.stateService.addRow();
    this.rowAdd.emit(newRow);
    this.cdr.markForCheck();

    // Navigate to the last page where the new row was appended
    const lastPage = Math.max(0, this.totalPages - 1);
    if (this.currentPage !== lastPage) {
      this.goToPage(lastPage);
    }

    this.a11yService.announceRowAdded(this.displayRows.length);

    setTimeout(() => {
      const newRowIndex = this.visibleRows.findIndex((r) => r.id === newRow.id);
      if (newRowIndex >= 0 && this.tableElement?.nativeElement) {
        const cell = this.tableElement.nativeElement.querySelector(
          `[data-row-id="${newRow.id}"][data-col-index="0"]`
        ) as HTMLElement;
        if (cell) {
          cell.focus();
          cell.scrollIntoView({ block: 'nearest' });
        }
      }
    }, 100);
  }

  onDeleteSelected(): void {
    if (this.pasteErrorLocked) return;
    if (this.selectedIds.length === 0) return;
    const count = this.selectedIds.length;
    const idsToDelete = [...this.selectedIds];
    const firstDeletedIdx = this.displayRows.findIndex(r => idsToDelete.includes(r.id));
    this.stateService.deleteRows(idsToDelete);
    this.a11yService.announceRowsDeleted(count);
    this.rowDelete.emit(idsToDelete);
    if (this.stateService.getState().rows.length === 0) {
      this.hasUserInteracted = false;
    }
    this.cdr.markForCheck();
    setTimeout(() => {
      if (this.visibleRows.length > 0) {
        const targetIdx = Math.min(firstDeletedIdx, this.visibleRows.length - 1);
        this.keyboardService.focusCellByPosition({ rowIndex: Math.max(0, targetIdx), columnIndex: 0 }, this.tableElement);
      }
    }, 0);
  }

  private finalizeRowMove(index: number, newIndex: number): void {
    this.stateService.moveRow(index, newIndex);
    const col = this.focusedCell?.columnIndex ?? 0;
    this.focusedCell = { rowIndex: newIndex, columnIndex: col };
    this.selectionService.cellRange = { anchorRow: newIndex, anchorCol: col, focusRow: newIndex, focusCol: col };
    this.a11yService.announceRowMoved(index + 1, newIndex + 1);
    this.rowReorder.emit({ fromIndex: index, toIndex: newIndex });
    this.cdr.markForCheck();
    setTimeout(() => {
      if (this.focusedCell) this.keyboardService.focusCellByPosition(this.focusedCell, this.tableElement);
    }, 0);
  }

  onMoveRowUp(rowId: string, event?: KeyboardEvent): void {
    if (this.pasteErrorLocked) return;
    event?.preventDefault();
    const index = this.displayRows.findIndex((r) => r.id === rowId);
    if (index <= 0) return;
    this.finalizeRowMove(index, index - 1);
  }

  onMoveRowDown(rowId: string, event?: KeyboardEvent): void {
    if (this.pasteErrorLocked) return;
    event?.preventDefault();
    const index = this.displayRows.findIndex((r) => r.id === rowId);
    if (index < 0 || index >= this.displayRows.length - 1) return;
    this.finalizeRowMove(index, index + 1);
  }

  // ============================================================
  // Cell editing — thin wrappers delegating to ChildTableEditingService
  // ============================================================

  startEditing(rowId: string, columnKey: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const started = this.editingService.startEditing(
      rowId, columnKey, this.config, this.columns, this.displayRows,
      this.visibleRows, this.visibleColumns,
      (pos) => { this.focusedCell = pos; },
      (label) => this.showReadonlyFieldAlert(label),
      this.pasteErrorLocked,
    );
    if (started) this.cdr.markForCheck();
  }

  /** Save quietly and emit events. Returns true if saved. */
  private saveEditQuietly(skipDomSync = false): boolean {
    const result = this.editingService.saveEditQuietly(
      this.displayRows, this.visibleRows, this.visibleColumns,
      this.columns, this.tableElement, skipDomSync,
    );
    this.handleSaveResult(result);
    if (result.editPosition) {
      this.focusedCell = { rowIndex: result.editPosition.rowIndex, columnIndex: result.editPosition.columnIndex };
    }
    this.cdr.markForCheck();
    setTimeout(() => { this.editingService._editTransitionActive = false; }, 0);
    return result.saved;
  }

  saveEdit(): void {
    const savedRowId = this.editingCell?.rowId;
    const savedColumnKey = this.editingCell?.columnKey;
    const result = this.editingService.saveEdit(
      this.displayRows, this.visibleRows, this.visibleColumns,
      this.columns, this.tableElement,
    );
    this.handleSaveResult(result);
    if (result.editPosition) {
      this.focusedCell = { rowIndex: result.editPosition.rowIndex, columnIndex: result.editPosition.columnIndex };
    }
    this.cdr.markForCheck();
    if (!result.saved) return;
    setTimeout(() => {
      this.editingService._editTransitionActive = false;
      if (this.tableElement?.nativeElement && savedRowId && savedColumnKey) {
        const cell = this.tableElement.nativeElement.querySelector(
          `.grid-cell[data-row-id="${savedRowId}"][data-col-key="${savedColumnKey}"]`
        ) as HTMLElement;
        cell?.focus();
      }
    }, 0);
  }

  cancelEdit(): void {
    const rowId = this.editingCell?.rowId;
    const columnKey = this.editingCell?.columnKey;
    const position = this.editingService.cancelEdit(this.visibleRows, this.visibleColumns);
    if (position) {
      this.focusedCell = position;
    }
    this.cdr.markForCheck();
    setTimeout(() => {
      this.editingService._editTransitionActive = false;
      if (this.tableElement?.nativeElement && rowId && columnKey) {
        const cell = this.tableElement.nativeElement.querySelector(
          `.grid-cell[data-row-id="${rowId}"][data-col-key="${columnKey}"]`
        ) as HTMLElement;
        cell?.focus();
      }
    }, 0);
  }

  onCellEditorSave(value: any, rowId: string, columnKey: string): void {
    this.editingService.editingValue = value;
    this.saveEdit();
  }

  onCellEditorTabSave(
    event: { value: any; shiftKey: boolean },
    rowId: string, columnKey: string,
    rowIndex: number, colIndex: number
  ): void {
    const result = this.editingService.onCellEditorTabSave(
      event, colIndex, this.displayRows, this.visibleRows,
      this.visibleColumns, this.columns, this.tableElement,
    );
    this.handleSaveResult(result);
    if (!result.saved) return;
    if (result.editPosition) {
      this.focusedCell = { rowIndex: result.editPosition.rowIndex, columnIndex: result.editPosition.columnIndex };
    }
    this.cdr.markForCheck();
    setTimeout(() => { this.editingService._editTransitionActive = false; }, 0);
    const targetCol = result.nextColIndex ?? colIndex;
    this.keyboardService.focusCellByPosition({ rowIndex: result.rowIndex ?? rowIndex, columnIndex: targetCol }, this.tableElement);
  }

  /** Process a SaveEditResult — emit events for cellChange and validationError. */
  private handleSaveResult(result: SaveEditResult): void {
    if (result.cellChange) {
      this.cellChange.emit(result.cellChange);
    }
    if (result.validationError) {
      this.validationError.emit(result.validationError);
    }
  }

  // ============================================================
  // Keyboard navigation — delegates to ChildTableKeyboardService
  // ============================================================

  onCellKeydown(event: KeyboardEvent, rowId: string, columnKey: string, rowIndex: number, colIndex: number): void {
    this.keyboardService.onCellKeydown(event, rowId, columnKey, rowIndex, colIndex, this.buildKeyboardContext());
  }

  onHeaderKeydown(event: KeyboardEvent, columnKey: string, colIndex: number): void {
    this.keyboardService.onHeaderKeydown(event, columnKey, colIndex, this.buildKeyboardContext());
  }

  onHeaderWidgetKeydown(event: KeyboardEvent, columnKey: string, colIndex: number, widgetType: string): void {
    this.keyboardService.onHeaderWidgetKeydown(event, columnKey, colIndex, widgetType, this.buildKeyboardContext());
  }

  private returnFocusAfterMenuClose(): void {
    this.keyboardService.returnFocusAfterMenuClose(this.buildKeyboardContext());
  }

  // ============================================================
  // Import
  // ============================================================

  onOpenImport(): void {
    if (this.pasteErrorLocked) return;
    this.openImportModal = true;
    this.cdr.markForCheck();
  }

  onImportModalClose(): void {
    this.openImportModal = false;
    this.cdr.markForCheck();
  }

  onImportModalComplete(result: ImportModalResult): void {
    this.openImportModal = false;
    this.hasUserInteracted = true;
    if (result.success && result.data && result.data.length > 0) {
      this.addRowsChunked(result.data).then(completed => {
        const importResult: ImportResult<T> = {
          success: true,
          data: result.data as T[],
          errors: [],
          warnings: []
        };
        this.importComplete.emit(importResult);
        this.resetPaginationAfterImport();
        if (completed) {
          this.a11yService.announce(`Imported ${result.recordsImported} rows`);
        }
        this.clearImportProgress();
      });
    } else if (!result.success) {
      this.a11yService.announce(`Import failed: ${result.error}`, 'assertive');
    }
    this.cdr.markForCheck();
  }

  async addRowsChunked(rows: any[], chunkSize = 50): Promise<boolean> {
    const total = rows.length;
    this.setImportProgress('adding', 0, total);
    this.a11yService.announce(`Importing ${total} rows`);
    let lastMilestone = 0;

    for (let i = 0; i < total; i += chunkSize) {
      if (this.importProgress?.cancelled) {
        this.clearImportProgress();
        return false;
      }
      const chunk = rows.slice(i, i + chunkSize);
      for (const item of chunk) this.stateService.addRow(item);
      const current = Math.min(i + chunkSize, total);
      this.updateImportProgress(current);

      const pct = Math.round((current / total) * 100);
      if (pct >= lastMilestone + 25) {
        lastMilestone = Math.floor(pct / 25) * 25;
        this.a11yService.announce(`${lastMilestone}% complete, ${current} of ${total} rows`);
      }

      await new Promise(r => requestAnimationFrame(r));
    }
    return true;
  }

  onToolbarTemplateDownload(): void {
    const cols = this.columns
      .filter((c) => c.type !== 'attachment' && c.type !== 'checkbox')
      .map((c) => ({ key: c.key, label: c.label, type: c.type }));
    const rows = this.displayRows.map((r) => r.data as Record<string, any>);
    const filename = `${this.config.title || 'data'}.csv`.toLowerCase().replace(/\s+/g, '_');
    this.importService.downloadCSV(cols, rows, filename);
    this.a11yService.announce('CSV downloaded');
  }

  // ============================================================
  // Column menu — delegates to ChildTableMenuService
  // ============================================================

  cleanupDropdownState(): void {
    this.menuService.closeColumnMenuState();
    this.cdr.markForCheck();
  }

  toggleColumnMenu(columnKey: string, event: Event): void {
    const colIdx = this.menuService.toggleColumnMenu(
      columnKey, event, this.pasteErrorLocked, this.visibleColumns,
      {
        onCloseAndRefocus: () => this.returnFocusAfterMenuClose(),
        onFocusFirstItem: () => this.columnMenuComp?.focusFirstItem(),
        getMenuElement: () => this.columnMenuComp?.nativeElement ?? undefined,
        detectChanges: () => this.cdr.detectChanges(),
      },
    );
    if (colIdx >= 0) this.keyboardService.headerInteractionColIndex = colIdx;
    if (this.headerInteractionMode) {
      this.keyboardService.setMenuOpenedFromInteractionMode(true);
    }
    this.cdr.markForCheck();
  }

  getUniqueColumnValues(columnKey: string): string[] {
    return this.menuService.getUniqueColumnValues(columnKey, this.displayRows);
  }

  onColumnFilterApply(columnKey: string, filter: { operator: string; value: string }): void {
    this.menuService.onColumnFilterApply(columnKey, filter);
    this.cdr.markForCheck();
    this.returnFocusAfterMenuClose();
  }

  cancelColumnMenu(columnKey: string): void {
    this.menuService.cancelColumnMenu(columnKey);
    this.cdr.markForCheck();
    this.returnFocusAfterMenuClose();
  }

  // ============================================================
  // Scroll handlers
  // ============================================================

  onTableWrapperScroll(_event: Event): void {
    // Reserved for future scroll-dependent behavior (e.g. sticky headers)
  }

  /**
   * Handle focus entering or moving within the grid.
   * - External entry (Tab in): announce grid context.
   * - Internal move NOT triggered by our code: VoiceOver moved its cursor,
   *   so sync focusedCell/cellRange to stay in sync with the VO position.
   */
  onGridFocusIn(event: FocusEvent): void {
    const related = event.relatedTarget as HTMLElement | null;
    const fromInside = related && this.tableElement?.nativeElement?.contains(related);
    const target = event.target as HTMLElement;

    if (!fromInside) {
      const isHeader = !!target.closest('.grid-header');
      const rows = this.displayRows.length;
      const cols = this.visibleColumns.length;
      if (isHeader) {
        this.a11yService.announce(
          `Table header. ${rows} rows, ${cols} columns. Use arrow keys to navigate columns. Tab to enter data.`
        );
        this.keyboardService.resetLastFocusedRowIndex(-1);
      } else {
        const rowIdx = Number.parseInt(target.dataset['rowIndex'] ?? '0', 10);
        const colIdx = Number.parseInt(target.dataset['colIndex'] ?? '0', 10);
        const colLabel = colIdx >= 0 ? this.visibleColumns[colIdx]?.label : '';
        const colInfo = colLabel ? `, ${colLabel} column` : '';
        this.a11yService.announce(
          `Data area. Row ${rowIdx + 1} of ${rows}${colInfo}. Use arrow keys to navigate. Enter to edit. Tab to exit grid.`
        );
        this.keyboardService.resetLastFocusedRowIndex(rowIdx);
      }
      return;
    }

    if (this.keyboardService._programmaticFocusActive) return;

    const cell = target.closest('.grid-cell') as HTMLElement;
    if (!cell) return;

    const rowRaw = cell.dataset['rowIndex'];
    const colRaw = cell.dataset['colIndex'];
    if (rowRaw === undefined || colRaw === undefined) return;

    const rowIndex = Number.parseInt(rowRaw, 10);
    const colIndex = Number.parseInt(colRaw, 10);

    this.focusedCell = { rowIndex, columnIndex: colIndex };
    this.selectionService.cellRange = {
      anchorRow: rowIndex, anchorCol: colIndex,
      focusRow: rowIndex, focusCol: colIndex,
    };
    this.keyboardService.updateRovingTabindex(cell, this.tableElement);
    this.keyboardService.announceFocusedCell({ rowIndex, columnIndex: colIndex }, this.buildKeyboardContext());
    this.cdr.markForCheck();
  }

  /** Clear stale focus/range state when focus leaves the grid entirely */
  onTableFocusOut(event: FocusEvent): void {
    // Skip during edit transitions: the cell-editor is being destroyed and
    // focus will be restored in a setTimeout -- don't nuke state in between.
    if (this.editingService._editTransitionActive) return;

    const related = event.relatedTarget as HTMLElement | null;
    if (!related || !this.tableElement?.nativeElement?.contains(related)) {
      this.selectionService.cellRange = null;
      this.focusedCell = null;
      this.cdr.markForCheck();
    }
  }

  // ============================================================
  // Keyboard context builder
  // ============================================================

  private buildKeyboardContext(): KeyboardContext<T> {
    return {
      displayRows: this.displayRows,
      visibleRows: this.visibleRows,
      columns: this.columns,
      visibleColumns: this.visibleColumns,
      config: this.config,
      hiddenColumns: this.hiddenColumns,
      tableElement: this.tableElement,
      isMac: this.isMac,
      pasteErrorLocked: this.pasteErrorLocked,
      onSort: (columnKey) => this.onSort(columnKey),
      onAddRow: () => this.onAddRow(),
      onDeleteSelected: () => this.onDeleteSelected(),
      onSelectAll: () => this.onSelectAll(),
      onRowSelect: (rowId) => this.onRowSelect(rowId),
      onMoveRowUp: (rowId, event?) => this.onMoveRowUp(rowId, event),
      onMoveRowDown: (rowId, event?) => this.onMoveRowDown(rowId, event),
      onManualSave: () => this.onManualSave(),
      onCopyAction: () => this.onCopyAction(),
      onCutAction: () => this.onCutAction(),
      onClearCells: () => this.onClearCells(),
      undoBadPaste: () => this.undoBadPaste(),
      toggleColumnMenu: (columnKey, event) => this.toggleColumnMenu(columnKey, event),
      openRowMenuWithTrigger: (rowId, rowIndex, trigger) => this.openRowMenuWithTrigger(rowId, rowIndex, trigger),
      startEditing: (rowId, columnKey, event?) => this.startEditing(rowId, columnKey, event),
      saveEditQuietly: (skipDomSync?) => this.saveEditQuietly(skipDomSync),
      saveEdit: () => this.saveEdit(),
      cancelEdit: () => this.cancelEdit(),
      clearCellRange: () => this.clearCellRange(),
      showReadonlyFieldAlert: (label) => this.showReadonlyFieldAlert(label),
      onInlineCheckboxToggle: (value, rowId, columnKey) => this.onInlineCheckboxToggle(value, rowId, columnKey),
      onFileRemove: (rowId, columnKey, event) => this.onFileRemove(rowId, columnKey, event),
      getCellValue: (row, columnKey) => this.getCellValue(row, columnKey),
      formatCellValueForA11y: (value, column) => this.formatCellValueForA11y(value, column),
      markForCheck: () => this.cdr.markForCheck(),
      isEditable: this.isEditable,
      focusAddRowButton: () => this.focusAddRowButton(),
    };
  }

  /** Focus the "+" add-row button. Returns true if it was found and focused. */
  private focusAddRowButton(): boolean {
    const btn = this.elementRef.nativeElement.querySelector('.add-row-btn') as HTMLElement;
    if (btn) {
      btn.focus();
      this.a11yService.announce('Add new row button');
      return true;
    }
    return false;
  }

  // ============================================================
  // Utility getters
  // ============================================================

  getCellValue(row: ChildTableRow<T>, columnKey: string): any {
    return (row.data as any)[columnKey];
  }

  getSelectLabel(column: ChildTableColumn<T>, value: any): string {
    if (!column.selectOptions) return String(value ?? '');
    const option = column.selectOptions.find((o) => String(o.value) === String(value));
    return option ? option.label : String(value ?? '');
  }

  getMultiselectLabel(column: ChildTableColumn<T>, value: any): string {
    if (!value) return '';
    const values = typeof value === 'string' ? value.split(',').map(v => v.trim()) : (Array.isArray(value) ? value : []);
    if (!column.selectOptions || values.length === 0) return String(value ?? '');
    const labels = values.map(v => column.selectOptions?.find(o => String(o.value) === String(v))?.label ?? v);
    return labels.join(', ');
  }

  /** Reverse-map a select trigger's display text back to its option value. */
  private reverseSelectLabel(column: ChildTableColumn<T>, displayText: string): string {
    if (!column.selectOptions || !displayText) return '';
    const opt = column.selectOptions.find((o) => o.label === displayText);
    return opt ? String(opt.value) : displayText;
  }

  /** Reverse-map a multiselect trigger's display text back to comma-separated values. */
  private reverseMultiselectLabel(column: ChildTableColumn<T>, displayText: string): string {
    if (!column.selectOptions || !displayText) return '';
    // The trigger shows "A, B" for <=2 items or "3 selected" for more
    const countMatch = /^(\d+) selected$/.exec(displayText);
    if (countMatch) {
      // Can't reverse "N selected" to actual values; return current stored value
      const row = this.displayRows.find((r) => r.id === this.editingService.editingCell?.rowId);
      return row ? String((row.data as any)[column.key] ?? '') : '';
    }
    const labels = displayText.split(',').map((s) => s.trim());
    const values = labels.map((lbl) => {
      const opt = column.selectOptions?.find((o) => o.label === lbl);
      return opt ? String(opt.value) : lbl;
    });
    return values.join(',');
  }

  onInlineCheckboxToggle(newValue: boolean, rowId: string, columnKey: string): void {
    this.stateService.updateCell(rowId, columnKey, newValue);
    this.cellChange.emit({ rowId, columnKey, value: newValue, oldValue: !newValue });
  }

  formatCellValueForA11y(value: any, column: ChildTableColumn<T>): string {
    if (value === null || value === undefined || value === '') return '';
    switch (column.type) {
      case 'currency': return this.formatCurrency(value);
      case 'number': return String(value);
      case 'date': return this.formatDate(value);
      case 'datetime': return this.formatDatetime(value);
      case 'checkbox': return value ? 'checked' : 'unchecked';
      case 'select': return this.getSelectLabel(column, value);
      case 'multiselect': return this.getMultiselectLabel(column, value);
      case 'pii': return 'masked value';
      case 'file':
      case 'attachment':
        if (typeof value === 'object' && value !== null) return value.name || 'file attached';
        return String(value);
      default: return String(value);
    }
  }

  /** Formatted cell value for screen reader announcements */
  getFormattedCellValue(row: ChildTableRow<T>, column: ChildTableColumn<T>): string {
    const raw = this.getCellValue(row, column.key);
    return this.formatCellValueForA11y(raw, column);
  }

  /**
   * Computed aria-label for a data cell.
   * Includes row number, column name and value so the screen reader
   * always provides full context regardless of navigation direction.
   */
  getCellAriaLabel(row: ChildTableRow<T>, column: ChildTableColumn<T>, rowIndex?: number): string {
    const value = this.getFormattedCellValue(row, column);
    const rowPart = rowIndex !== undefined ? `Row ${this.getGlobalRowNumber(rowIndex)}, ` : '';
    return `${rowPart}${column.label || column.key}, ${value || 'blank'}`;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);
  }

  formatDate(value: string | Date): string {
    if (!value) return '';
    const d = this.parseLocalDate(value);
    if (!d || Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString();
  }

  formatDatetime(value: string | Date): string {
    if (!value) return '';
    const d = this.parseLocalDate(value);
    if (!d || Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-US') + ' ' +
      String(d.getHours()).padStart(2, '0') + ':' +
      String(d.getMinutes()).padStart(2, '0');
  }

  /**
   * Parse a date string into a local-time Date, avoiding the UTC shift
   * that `new Date("YYYY-MM-DD")` causes (ISO date-only = UTC midnight).
   */
  private parseLocalDate(value: string | Date): Date | null {
    if (value instanceof Date) return value;
    if (!value) return null;
    const s = String(value).trim();
    // ISO format: "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm..."
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
      return new Date(s.includes('T') ? s : s + 'T00:00:00');
    }
    // US format: "MM/DD/YYYY" or "MM/DD/YYYY HH:mm..."
    const parts = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(.*)$/.exec(s);
    if (parts) {
      const base = new Date(+parts[3], +parts[1] - 1, +parts[2]);
      const timePart = parts[4]?.trim();
      if (timePart) {
        const t = /(\d{1,2}):(\d{2})/.exec(timePart);
        if (t) {
          let h = +t[1];
          if (/pm/i.test(timePart) && h < 12) h += 12;
          if (/am/i.test(timePart) && h === 12) h = 0;
          base.setHours(h, +t[2]);
        }
      }
      return base;
    }
    return new Date(s);
  }

  trackByRowId(index: number, row: ChildTableRow<T>): string { return row.id; }
  trackByColumnKey(index: number, column: ChildTableColumn<T>): string { return column.key; }

  /** Global 1-based row number accounting for current page offset. */
  getGlobalRowNumber(rowIndex: number): number {
    return this.currentPage * this.pageSize + rowIndex + 1;
  }

  get totalCount(): number { return this.displayRows.length; }
  get keyboardShortcuts(): string[] { return this.a11yService.getKeyboardShortcutsHelp(); }

  get totalRowCount(): number {
    return this.stateService.getState().rows.length;
  }

  get activeFilters(): FilterConfig[] {
    return this.stateService.getState().filters;
  }

  get hasActiveFilters(): boolean {
    return this.activeFilters.length > 0 || !!this.searchTerm;
  }

  removeFilter(columnKey: string): void {
    this.stateService.removeFilter(columnKey);
    this.a11yService.announce('Filter removed for ' + this.getColumnLabel(columnKey));
    this.cdr.markForCheck();
  }

  clearSearchTerm(): void {
    this.searchTerm = '';
    this.stateService.setSearchTerm('');
    if (this.config?.serverSidePagination) {
      this.searchChange.emit('');
    }
    this.a11yService.announce('Search cleared');
    this.cdr.markForCheck();
  }

  private getColumnLabel(key: string): string {
    return this.columns.find(c => c.key === key)?.label ?? key;
  }

  hasColumnFilter(columnKey: string): boolean {
    return this.activeFilters.some(f => f.column === columnKey);
  }

  getFilterLabel(filter: FilterConfig): string {
    const col = this.getColumnLabel(filter.column);
    const op = filter.operator.replace(/([A-Z])/g, ' $1').toLowerCase();
    return filter.value ? `${col} ${op} "${filter.value}"` : `${col} ${op}`;
  }

  get totalRecordsCount(): number {
    if (this.config?.serverSidePagination) {
      const total = Number(this.totalRecords);
      if (Number.isFinite(total) && total >= 0) return total;
    }
    return this.displayRows.length;
  }

  get totalPages(): number {
    const size = Math.max(1, Number(this.pageSize) || 1);
    const total = this.totalRecordsCount;
    if (total <= 0) return 1;
    return Math.max(1, Math.ceil(total / size));
  }

  get paginationStart(): number {
    const total = this.totalRecordsCount;
    if (total <= 0) return 0;
    return this.currentPage * this.pageSize + 1;
  }

  get paginationEnd(): number {
    const total = this.totalRecordsCount;
    if (total <= 0) return 0;
    return Math.min((this.currentPage + 1) * this.pageSize, total);
  }

  get visibleRows(): ChildTableRow<T>[] {
    // Server-side pagination: parent passes already-paged data; do NOT slice locally.
    if (this.config?.serverSidePagination) {
      return this.displayRows;
    }
    const start = this.currentPage * this.pageSize;
    return this.displayRows.slice(start, start + this.pageSize);
  }

  get visibleColumns(): ChildTableColumn<T>[] {
    return this.columns.filter((c) => !this.hiddenColumns.has(c.key));
  }

  isColumnRequired(column: ChildTableColumn<T>): boolean {
    return !!column.validation?.some(v => v.type === 'required');
  }

  // ============================================================
  // Cover State — Public API
  // ============================================================

  clearAllFilters(): void {
    if (this.searchTerm) {
      this.searchTerm = '';
      this.stateService.setSearchTerm('');
      if (this.config?.serverSidePagination) {
        this.searchChange.emit('');
      }
    }
    const filterColumns = this.activeFilters.map(f => f.column);
    for (const col of filterColumns) {
      this.stateService.removeFilter(col);
    }
    this.a11yService.announce('All filters cleared');
    this.cdr.markForCheck();
  }

  dismissError(): void {
    this.recoverableError = null;
    this.cdr.markForCheck();
  }

  retryError(): void {
    const action = this.recoverableError?.retryAction;
    this.recoverableError = null;
    this.cdr.markForCheck();
    if (action) action();
  }

  /** Allow parent to trigger an error cover. */
  setError(error: CoverError): void {
    this.recoverableError = error;
    this.cdr.markForCheck();
  }

  /** Allow parent to toggle import progress cover. */
  setImporting(active: boolean): void {
    this.isImportInProgress = active;
    if (!active) this.importProgress = null;
    this.cdr.markForCheck();
  }

  private lastAnnouncedMilestone = 0;

  setImportProgress(phase: 'adding' | 'saving', current: number, total: number): void {
    this.importProgress = { phase, current, total, cancelled: false };
    this.isImportInProgress = true;
    this.lastAnnouncedMilestone = 0;
    if (phase === 'saving') {
      this.a11yService.announce(`Saving ${total} batches to server`);
    }
    this.cdr.markForCheck();
  }

  updateImportProgress(current: number): void {
    if (!this.importProgress) return;
    this.importProgress.current = current;
    const pct = this.importProgress.total > 0
      ? Math.round((current / this.importProgress.total) * 100) : 0;
    if (pct >= this.lastAnnouncedMilestone + 25) {
      this.lastAnnouncedMilestone = Math.floor(pct / 25) * 25;
      this.a11yService.announce(`${this.lastAnnouncedMilestone}% complete`);
    }
    this.cdr.markForCheck();
  }

  cancelImport(): void {
    if (this.importProgress) {
      this.importProgress.cancelled = true;
    }
  }

  clearImportProgress(): void {
    const wasAdding = this.importProgress?.phase === 'adding';
    const wasCancelled = this.importProgress?.cancelled;
    const added = this.importProgress?.current ?? 0;
    const total = this.importProgress?.total ?? 0;
    this.importProgress = null;
    this.isImportInProgress = false;
    this.cdr.markForCheck();
    if (wasCancelled) {
      const msg = wasAdding
        ? `Import cancelled. ${added} of ${total} rows were added.`
        : `Save cancelled. ${added} of ${total} batches completed.`;
      this.a11yService.announce(msg, 'assertive');
    }
  }

  /** Allow parent to toggle loading cover. */
  setLoading(active: boolean): void {
    this.isSearchingOrLoading = active;
    this.cdr.markForCheck();
  }

  /** Announce cover state transitions so screen readers convey the current view. */
  private announceCoverStateTransition(): void {
    const next = this.coverState;
    if (next === this.previousCoverState) return;
    this.previousCoverState = next;

    switch (next) {
      case 'filteredEmpty': {
        const total = this.totalRowCount;
        this.a11yService.announce(
          `No matching records. ${total} record${total === 1 ? '' : 's'} hidden by filters.`,
        );
        break;
      }
      case 'empty':
        this.a11yService.announce(`No ${this.title ?? this.config?.title ?? 'records'} yet. Add your first entry or import data to get started.`);
        break;
      case 'loading':
        this.a11yService.announce('Searching for records.');
        break;
      case 'importing':
        this.a11yService.announce('Import in progress.');
        break;
      case 'error':
        this.a11yService.announce(
          this.recoverableError?.message ?? 'An error occurred.',
          'assertive',
        );
        break;
      case 'saveBlocked':
        this.a11yService.announce('Changes blocked. Undo or dismiss to continue.', 'assertive');
        break;
      case 'ready':
        break;
    }
  }

}
